import { useState } from "react";
import { useVoice, VOICE_CATEGORIES } from "@/contexts/VoiceContext";
import { Volume2, Loader2, Check, ChevronRight, Globe, Mic } from "lucide-react";
import { cn } from "@/lib/utils";

export function VoiceOnboarding({ onComplete }) {
  const { voice, setVoice, speakText, speaking, stopSpeaking, setTtsEnabled } = useVoice();
  const [previewing, setPreviewing] = useState(null);
  const [step, setStep] = useState(0); // 0 = pick voice, 1 = confirm

  const flatVoices = VOICE_CATEGORIES.flatMap(c =>
    c.voices.map(v => ({ ...v, category: c.label }))
  );

  const previewVoice = async (voiceKey) => {
    if (speaking) stopSpeaking();
    setPreviewing(voiceKey);
    setVoice(voiceKey);
    await speakText("Hello! I'm Omni, your companion. Let's make today a great one.", `onboard-${voiceKey}`);
    setPreviewing(null);
  };

  const handleConfirm = () => {
    setTtsEnabled(true);
    localStorage.setItem("omni_voice_onboarded", "true");
    onComplete();
  };

  const handleSkip = () => {
    localStorage.setItem("omni_voice_onboarded", "true");
    onComplete();
  };

  if (step === 1) {
    const chosen = flatVoices.find(v => v.key === voice);
    return (
      <div
        data-testid="voice-onboarding-confirm"
        className="max-w-md w-full mx-auto bg-card border border-primary/20 rounded-3xl p-6 shadow-lg animate-in fade-in zoom-in-95 duration-300"
      >
        <div className="text-center mb-5">
          <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-3">
            <Volume2 className="w-6 h-6 text-primary" />
          </div>
          <h3 className="text-lg font-heading font-semibold font-emphasis">You've chosen {chosen?.label}</h3>
          <p className="text-sm text-muted-foreground mt-1">{chosen?.desc} — {chosen?.origin}</p>
        </div>
        <div className="flex items-center gap-3 p-3 rounded-2xl bg-primary/5 border border-primary/10 mb-5">
          <Mic className="w-5 h-5 text-primary flex-shrink-0" />
          <div className="text-xs text-muted-foreground">
            <span className="text-foreground font-medium">Voice is everywhere.</span> Use the mic button on any input to speak, and Omni will reply in {chosen?.label}'s voice.
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setStep(0)}
            className="flex-1 py-2.5 rounded-2xl border border-border/50 text-sm text-muted-foreground hover:text-foreground hover:bg-muted/30 transition-all"
          >
            Change voice
          </button>
          <button
            data-testid="voice-onboarding-confirm-btn"
            onClick={handleConfirm}
            className="flex-1 py-2.5 rounded-2xl bg-primary text-primary-foreground text-sm font-medium shadow-md hover:shadow-lg transition-all"
          >
            Let's go
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid="voice-onboarding-card"
      className="max-w-md w-full mx-auto bg-card border border-primary/20 rounded-3xl p-5 shadow-lg animate-in fade-in zoom-in-95 duration-300"
    >
      <div className="text-center mb-4">
        <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-2">
          <Volume2 className="w-5 h-5 text-primary" />
        </div>
        <h3 className="text-lg font-heading font-semibold font-emphasis">Choose Omni's Voice</h3>
        <p className="text-xs text-muted-foreground mt-1">Pick how your companion sounds. You can always change this later.</p>
      </div>

      <div className="space-y-1.5 max-h-[320px] overflow-y-auto pr-1">
        {flatVoices.map(v => {
          const isSelected = voice === v.key;
          const isPreviewActive = previewing === v.key;
          return (
            <div
              key={v.key}
              data-testid={`voice-onboard-${v.key}`}
              onClick={() => setVoice(v.key)}
              className={cn(
                "w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-all cursor-pointer",
                isSelected
                  ? "bg-primary/10 border border-primary/20"
                  : "hover:bg-muted/30 border border-transparent"
              )}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className={cn("text-sm font-medium", isSelected && "text-primary")}>{v.label}</span>
                  <span className="text-[10px] text-muted-foreground/70 flex items-center gap-0.5">
                    <Globe className="w-2.5 h-2.5" />
                    {v.origin}
                  </span>
                </div>
                <span className="text-[11px] text-muted-foreground">{v.desc} · {v.gender}</span>
              </div>
              <div
                data-testid={`voice-onboard-preview-${v.key}`}
                role="button"
                tabIndex={0}
                onClick={(e) => { e.stopPropagation(); previewVoice(v.key); }}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); previewVoice(v.key); } }}
                className={cn(
                  "w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 transition-all cursor-pointer",
                  isPreviewActive
                    ? "bg-primary text-primary-foreground"
                    : "bg-muted/50 text-muted-foreground hover:bg-primary/10 hover:text-primary"
                )}
              >
                {isPreviewActive ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Volume2 className="w-3.5 h-3.5" />}
              </div>
              {isSelected && (
                <div className="w-6 h-6 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
                  <Check className="w-3.5 h-3.5 text-primary-foreground" />
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="flex gap-2 mt-4">
        <button
          data-testid="voice-onboarding-skip"
          onClick={handleSkip}
          className="flex-1 py-2.5 rounded-2xl border border-border/50 text-sm text-muted-foreground hover:text-foreground hover:bg-muted/30 transition-all"
        >
          Skip for now
        </button>
        <button
          data-testid="voice-onboarding-next"
          onClick={() => setStep(1)}
          className="flex-1 py-2.5 rounded-2xl bg-primary text-primary-foreground text-sm font-medium shadow-md hover:shadow-lg transition-all flex items-center justify-center gap-1"
        >
          Continue <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
